<template>
    <modal-side-generic
    :title="t('payments.batchfile.newPayment.reusePaymentsModal.title')"
    :subTitle="t('payments.batchfile.newPayment.reusePaymentsModal.subTitle')"
    :sizeClass="'big'"
    :tabs="tabsList"
    @tabClicked="tabClickHandler"
    @hideMe="emit('hideMe')">
        <!-- Table -->
        <table-generic
        v-if="!selectedTab.showLoader && !selectedTab.hasError && selectedTab.table.data.length"
        :key="selectedTab.id"
        :tableStyles="selectedTab.table.styles"
        :headerData="selectedTab.table.headerData"
        :tableData="selectedTab.table.data"
        :hasMore="selectedTab.table.hasMore && selectedTab.id !== 'latest'"
        :isLoadingMore="tableLoadingMore"
        :loadingMoreText="t('payments.batchfile.newPayment.reusePaymentsModal.loadingMoreText')"
        @sortBy="tableSortByHandler"
        @loadMore="loadMoreHandler"
        @rowClicked="rowClickHandler"/>
        <!-- Empty State -->
        <empty-state-generic
        v-if="!selectedTab.showLoader && !selectedTab.hasError && !selectedTab.table.data.length"
        class="reuse-transfer-modal-empty-state"
        :title="selectedTab.emptyState.title"
        :description="selectedTab.emptyState.description"
        :illustration="selectedTab.emptyState.illustration"/>
        <!-- Loader Generic -->
        <div v-if="showLatestPaymentsLoader || showFavoritesLoader" class="loader-wrapper">
            <loaders-generic/>
        </div>
        <!-- Feedback Toast -->
        <toast-generic
        v-if="showFeedbackToast"
        :toastType="feedbackToastData.toastType"
        :feedbackText="feedbackToastData.text"
        :buttonsParams="feedbackToastData.buttons"
        @hideMe="showFeedbackToast = false"/>
        <!-- Error State -->
        <empty-state-error v-if="selectedTab.hasError" :message="t('genericErrorMessage')"/>
    </modal-side-generic>
</template>

<script setup>
import ModalSideGeneric from '@/components/generic/modals/side/Generic'
import TableGeneric from '@/components/generic/table/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'
import LoadersGeneric from '@/components/generic/loaders/Generic'
import EmptyStateError from '@/components/generic/emptyStates/Error'

/* Vue & Helpers */
import { ref, computed } from 'vue'
import { sortTableData } from '@/assets/scripts/parsers/tables'
import { getDate } from '@/assets/scripts/dateBuilders'
import { ibanFormat, accountFormatHelper } from '@/assets/scripts/strings'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { requestBatchfileTransfersList, requestFavoritesList } = mapActions(moduleMapper.PAYMENTS_BATCHFILE_NEW_PAYMENT)

const {
    getBatchfileTransferList,
    getBatchfileTransferListHasMore,
    getFavoritesList,
    getFavoritesListHasMore
} = mapGetters(moduleMapper.PAYMENTS_BATCHFILE_NEW_PAYMENT)

/* Emmitters */
const emit = defineEmits(['hideMe', 'reuseSelectedPayment'])

/* Generic Data */
const showLatestPaymentsLoader = ref(false)
const showFavoritesLoader = ref(false)

/*************************************************
*                                                *
*                   Table Data                   *
*                                                *
*************************************************/
const latestTransfersList = ref(null)
const favoritesList = ref(null)

/** Latest Transfers Table Data */
const latestTransfersTableStyles = ref([
    null,
    { 'text-align': 'end' },
    { width: '350px%', 'text-align': 'end' }
])

const latestTransfersHeaderData = ref([
	{ label: t('payments.batchfile.newPayment.reusePaymentsModal.tableHeaders.name'), dataAttr: 'name', showInMobile: { groupWith: 'totalAmount' }, rowCellType: 'title', sortable: true },
	{ label: t('payments.batchfile.newPayment.reusePaymentsModal.tableHeaders.totalAmount'), dataAttr: 'totalAmount', rowCellType: 'amount', sortable: true },
    { label: t('payments.batchfile.newPayment.reusePaymentsModal.tableHeaders.registryNumber'), dataAttr: 'registryNumber', sortable: true }
])

const parsedLatestTransfersList = () => {
    latestTransfersList.value = getBatchfileTransferList.value.filter(obj => obj.status !== 'ERROR').map(transfer => {
        return [
            { value: transfer.id, dataAttr: 'id' },
            { value: transfer.sourceReference, dataAttr: 'name' },
            { type: 'amount', value: { content: transfer.totalAmount || '-', currency: transfer.currency, noBalanceIcon: true }, dataAttr: 'totalAmount' },
            { value: transfer.entryCount || '-', dataAttr: 'registryNumber' }
        ]
    })
}

const tableSortByHandler = (event) => {
    latestTransfersList.value = sortTableData(latestTransfersList.value, event)
}

/** Favorites Table Data */
const favoritesTableStyles = ref([
    { width: '50%' },
    { width: '15%', 'text-align': 'end' },
    { width: '35%', 'text-align': 'end' }
])

const favoritesHeaderData = ref([
    { label: t('payments.batchfile.newPayment.reusePaymentsModal.tableHeaders.name'), dataAttr: 'name', showInMobile: { groupWith: 'totalAmount' }, rowCellType: 'title', sortable: true },
	{ label: t('payments.batchfile.newPayment.reusePaymentsModal.tableHeaders.totalAmount'), dataAttr: 'totalAmount', rowCellType: 'amount', sortable: true },
    { label: t('payments.batchfile.newPayment.reusePaymentsModal.tableHeaders.registryNumber'), dataAttr: 'registryNumber', sortable: true }
])

const parsedFavoritesList = () => {
    favoritesList.value = getFavoritesList.value.map(favorite => {
        const params = favorite.customerOperationView.parameters
        const amount = getParameterValue(params, 'amount')
        const genericDestination = getParameterValue(params, 'beneficiaryAccountNumber')
        const isIBAN = !Number(genericDestination)

        return [
            { value: favorite.id, dataAttr: 'id' },
            { type: 'title', value: favorite.alias && favorite.alias.length ? favorite.alias : '-', dataAttr: 'description' },
            { type: 'amount', value: { content: parseFloat(amount), currency: getParameterValue(params, 'currencyDestination'), noBalanceIcon: true }, dataAttr: 'amount' },
            {
                value: genericDestination !== '-' ? (isIBAN ? ibanFormat(genericDestination) : genericDestination) : '-',
                dataAttr: 'genericDestination'
            }
        ]
    })
}

const getParameterValue = (paramsList, paramName) => {
    const targetParam = paramsList.find(param => param.name === paramName)
    return targetParam && targetParam.value ? targetParam.value : '-'
}

const rowClickHandler = event => {
    let data
    if (selectedTab.value.id === 'latest') {
        const selectedTransfer = getBatchfileTransferList.value.find(transfer => transfer.id === event.rowId)

        data = selectedTransfer
    } else {
        const selectedFavorite = getFavoritesList.value.find(favorite => favorite.id === event.rowId).customerOperationView.parameters

        data = selectedFavorite
    }

	emit('reuseSelectedPayment', data)
    emit('hideMe')
}

/*************************************************
*                                                *
*                   Modal Tabs                   *
*                                                *
*************************************************/
const selectedTabId = ref('latest')

const tabsList = computed(() => {
    return [
        {
            id: 'latest',
            label: t('payments.batchfile.newPayment.reusePaymentsModal.latestTab'),
            table: {
                data: latestTransfersList.value,
                headerData: latestTransfersHeaderData.value,
                styles: latestTransfersTableStyles.value,
                hasMore: getBatchfileTransferListHasMore.value
            },
            selected: selectedTabId.value === 'latest',
            emptyState: {
                title: t('payments.batchfile.newPayment.reusePaymentsModal.emptyState.latest.title'),
                description: t('payments.batchfile.newPayment.reusePaymentsModal.emptyState.latest.description'),
                illustration: 'emptyState/no-movements'
            },
            hasError: latestTransfersError.value,
            showLoader: showLatestPaymentsLoader.value
        },
        {
            id: 'favorites',
            label: t('payments.batchfile.newPayment.reusePaymentsModal.favoritesTab'),
            table: {
                data: favoritesList.value,
                headerData: favoritesHeaderData.value,
                styles: favoritesTableStyles.value,
                hasMore: getFavoritesListHasMore.value
            },
            selected: selectedTabId.value === 'favorites',
            emptyState: {
                title: t('payments.batchfile.newPayment.reusePaymentsModal.emptyState.favorites.title'),
                description: t('payments.batchfile.newPayment.reusePaymentsModal.emptyState.favorites.description'),
                illustration: 'emptyState/favorites-folder'
            },
            hasError: favoritesError.value,
            showLoader: showFavoritesLoader.value
        }
    ]
})

const selectedTab = computed(() => {
    return tabsList.value.find(tab => tab.selected)
})

const tabClickHandler = (event) => {
    selectedTabId.value = event
}

/*************************************************
*                                                *
*                 Modal Requests                 *
*                                                *
*************************************************/

const pageNumber = ref(1)
const tableLoadingMore = ref(false)

const reusePaymentsModalRequests = () => {
    requestLatestTransfers()
    requestFavorites()
}

const requestLatestTransfers = async () => {
    showLatestPaymentsLoader.value = true
    latestTransfersError.value = false

    try {
        await requestBatchfileTransfersList({ pageSize: 10, pageNumber: pageNumber.value })
        parsedLatestTransfersList()
        showLatestPaymentsLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        latestTransfersError.value = true
        showLatestPaymentsLoader.value = false
		errorHandler(error.response.data, requestLatestTransfers)
    }
}

const requestFavorites = async () => {
    pageNumber.value === 1 ? showFavoritesLoader.value = true : tableLoadingMore.value = true
    favoritesError.value = false

    try {
        await requestFavoritesList({
            pageNumber: pageNumber.value,
            operationTypes: 36004,
            pageSize: 10
        })
        parsedFavoritesList()
        showFavoritesLoader.value = false
        tableLoadingMore.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        showFavoritesLoader.value = false
        favoritesError.value = true
		errorHandler(error.response.data, requestFavorites)
    }
}

const loadMoreHandler = () => {
    pageNumber.value++
    selectedTabId.value === 'latest' ? requestLatestTransfers() : requestFavorites()
}

/*************************************************
*                                                *
*                  Error Handler                 *
*                                                *
*************************************************/
const favoritesError = ref(false)
const latestTransfersError = ref(false)
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

reusePaymentsModalRequests()
</script>

<style lang="scss" scoped>
.loader-wrapper {
    width: 100%;
    height: 100%;

	display: flex;
	justify-content: center;
	align-items: center;
}
</style>
